// MacronutrientChart.jsx
import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto'; // Simplified import
import PropTypes from 'prop-types';

const MacronutrientChart = ({ carbsGrams, proteinGrams, fatGrams }) => {
    const chartRef = useRef(null); // Ref to store the chart instance
    const canvasRef = useRef(null); // Ref for the canvas element

    // Determine if there's data to display the chart. This logic is moved outside
    // useEffect to be available for the main render return.
    const carbsCalories = carbsGrams * 4;
    const proteinCalories = proteinGrams * 4;
    const fatCalories = fatGrams * 9;
    const totalMacroCalories = carbsCalories + proteinCalories + fatCalories;

    // Condition to check if a chart can be meaningfully displayed
    const canDisplayChart = totalMacroCalories > 0 && (carbsGrams > 0 || proteinGrams > 0 || fatGrams > 0);

    useEffect(() => {
        // This is the single, well-defined cleanup function for this effect.
        const cleanupChart = () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };

        if (!canvasRef.current) {
            // If canvas is not yet available, ensure any lingering chart is cleaned up.
            cleanupChart();
            return cleanupChart; // Still return the cleanup function.
        }

        if (!canDisplayChart) {
            // If there's no data to display, clean up any existing chart and exit the effect.
            cleanupChart();
            return cleanupChart; // Return the cleanup function.
        }

        // If we reach here, canvasRef.current exists and canDisplayChart is true.
        // Proceed to create or update the chart.

        const macroPercentagesForChartData = {
            carbs: (carbsCalories / totalMacroCalories) * 100,
            protein: (proteinCalories / totalMacroCalories) * 100,
            fat: (fatCalories / totalMacroCalories) * 100,
        };

        const chartData = {
            labels: [`Carbs (${carbsGrams.toFixed(1)}g)`, `Protein (${proteinGrams.toFixed(1)}g)`, `Fat (${fatGrams.toFixed(1)}g)`],
            datasets: [{
                data: [macroPercentagesForChartData.carbs, macroPercentagesForChartData.protein, macroPercentagesForChartData.fat],
                backgroundColor: [
                    'rgba(14, 165, 233, 0.8)', // Sky Blue for Carbs
                    'rgba(79, 70, 229, 0.8)',  // Indigo for Protein
                    'rgba(245, 158, 11, 0.8)'  // Amber for Fat
                ],
                borderColor: [
                    'rgba(14, 165, 233, 1)',
                    'rgba(79, 70, 229, 1)',
                    'rgba(245, 158, 11, 1)'
                ],
                borderWidth: 1,
                hoverOffset: 8
            }]
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        padding: 20,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        font: { size: 13 }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            // context.raw contains the percentage value from the dataset
                            return `${context.label}: ${context.raw.toFixed(1)}% of calories`;
                        }
                    },
                    padding: 10,
                    titleFont: { size: 14 },
                    bodyFont: { size: 12 }
                },
            },
            animation: {
                animateScale: true,
                animateRotate: true,
                duration: 1000
            }
        };

        // Destroy previous chart instance if it exists before creating a new one.
        // This is implicitly handled by `cleanupChart` if it were the first thing,
        // but explicit here is also fine since we are about to re-assign.
        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null; // Good practice to nullify after destroy
        }

        // Create new chart instance
        const ctx = canvasRef.current.getContext('2d');
        chartRef.current = new Chart(ctx, {
            type: 'doughnut',
            data: chartData,
            options: chartOptions
        });

        // Return the single cleanup function.
        // This will be called when the component unmounts or before the effect runs again.
        return cleanupChart;

    }, [carbsGrams, proteinGrams, fatGrams, canDisplayChart, carbsCalories, proteinCalories, fatCalories, totalMacroCalories]); // Dependencies for the effect

    // Conditional rendering based on canDisplayChart
    if (!canDisplayChart) {
        return (
            <div style={{
                position: 'relative',
                height: '100px', // Adjusted for a message
                width: '100%',
                maxWidth: '300px',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6c757d',
                fontSize: '0.8rem',
                textAlign: 'center',
                border: '1px dashed #dee2e6',
                borderRadius: '0.25rem',
                padding: '10px'
            }}>
                No macronutrient data to display chart.
            </div>
        );
    }

    // Render the canvas for the chart if data is available
    return (
        <div style={{ position: 'relative', height: '300px', width: '100%', maxWidth: '300px', margin: '0 auto' }}>
            <canvas ref={canvasRef}></canvas>
        </div>
    );
};

MacronutrientChart.propTypes = {
    carbsGrams: PropTypes.number.isRequired,
    proteinGrams: PropTypes.number.isRequired,
    fatGrams: PropTypes.number.isRequired,
};

// It's good practice to also add defaultProps if not all props are always required,
// but since they are isRequired, this is fine.
// MacronutrientChart.defaultProps = {
//     carbsGrams: 0,
//     proteinGrams: 0,
//     fatGrams: 0,
// };

export default MacronutrientChart;